import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react"
import classNames from "classnames"
import { useWindowSize } from "../../hooks/useWindowSize"

export interface ContentItem {
  /**
   * The content item's name in the format that will be used as a slug in the url
   */
  slug: string
  /**
   * the display label of the button for the content item
   */
  buttonText: string
  /**
   * The node of content to be displayed.
   */
  children: ReactNode
  /**
   * HTML
   */
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
}

export interface ContentSwitcherProps {
  /**
   * This will be used to set the name of the URL param that lets the switcher know which item to display
   */
  param: string
  /**
   * The content item is the react node and associated labels
   */
  contentItems: ContentItem[]
}

const ContentSwitcher = ({
  contentItems,
  param = "solution",
}: ContentSwitcherProps) => {
  const [currentAccordionFocus, setCurrentAccordionFocus] = useState<number>()

  const [openItemIndex, setOpenItemIndex] = useState<number>(null)
  const [currentItemQuery, setCurrentItemQuery] = useState<string>(null)
  const switcherRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef({})
  const accordionRefs = useRef({})
  const [isMobile, setIsMobile] = useState(false)
  const { width } = useWindowSize()

  useEffect(() => {
    setIsMobile(window?.matchMedia("(max-width: 768px)")?.matches)
  }, [width])

  // Sets the initial open/current tile based on the url query param
  useEffect(() => {
    const params = new URLSearchParams(document.location.search)
    const initialContent = params.get(param)
    const initialContentIndex = contentItems.findIndex(
      (item) => item.slug === initialContent
    )
    if (initialContentIndex > -1 && initialContentIndex !== openItemIndex) {
      if (isMobile) {
        accordionRefs.current?.[initialContentIndex]?.focus()
      } else {
        tabRefs.current?.[initialContentIndex]?.focus()
      }
      setCurrentAccordionFocus((prev) =>
        isMobile ? initialContentIndex : prev
      )
      handleSwitcherChange(initialContentIndex)
    }
  }, [switcherRef, contentItems])

  useEffect(() => {
    const updateUrl = () => {
      const params = new URLSearchParams(document.location.search)
      if (!currentItemQuery) {
        params.delete(param)
        history.pushState({}, "", `?${params.toString()}`)
        return
      }
      params.set(param, currentItemQuery)
      history.pushState({}, "", `?${params.toString()}`)
    }
    updateUrl()
  }, [currentItemQuery])

  useEffect(() => {
    setCurrentAccordionFocus((prev) => {
      // this is to ensure that if an accordion item is open, and then clicked,
      // it will retain focus, rather than setting focus to null
      if (prev !== null && !openItemIndex) return prev
      return openItemIndex
    })
  }, [openItemIndex])

  useEffect(() => {
    if (currentAccordionFocus !== undefined && currentAccordionFocus !== null) {
      accordionRefs.current[currentAccordionFocus]?.focus()
    }
  }, [currentAccordionFocus])

  useEffect(() => {
    setCurrentAccordionFocus((prev) => {
      return isMobile ? null : prev
    })
  }, [isMobile])

  // this handles the tab panes on desktop
  const handleKeyDown = useCallback(
    (event) => {
      if (isMobile) return
      const getNextIndex = (event) => {
        switch (event.key) {
          case "ArrowLeft":
          case "ArrowUp":
            event.preventDefault()
            return openItemIndex === 0
              ? contentItems.length - 1
              : openItemIndex - 1
          case "ArrowRight":
          case "ArrowDown":
            event.preventDefault()
            return openItemIndex === contentItems.length - 1
              ? 0
              : openItemIndex + 1
          case "Home":
            return 0
          case "End":
            return contentItems.length - 1
          default:
            // this is to avoid overriding behaviour for tab / shift+tab
            return null
        }
      }
      const nextIndex = getNextIndex(event)
      if (nextIndex === null) return

      handleSwitcherChange(nextIndex)

      tabRefs.current?.[getNextIndex(event)]?.focus()
    },
    [openItemIndex, handleSwitcherChange, contentItems]
  )

  // this handles the accordion on mobile
  const handleKeyDownAccordion = useCallback(
    (event) => {
      const getFocusIndex = (event, prev) => {
        switch (event.key) {
          case "ArrowLeft":
          case "ArrowUp":
            event.preventDefault()
            return prev === 0
              ? contentItems.length - 1
              : prev === null
              ? prev
              : prev - 1
          case "ArrowRight":
          case "ArrowDown":
            event.preventDefault()
            return prev === contentItems.length - 1 || prev === null
              ? 0
              : prev + 1
          case "Home":
            return 0
          case "End":
            return contentItems.length - 1
          default:
            return prev
        }
      }

      setCurrentAccordionFocus((prev) => getFocusIndex(event, prev))
    },
    [currentAccordionFocus, contentItems]
  )

  useEffect(() => {
    if (switcherRef.current) {
      const switcher = switcherRef.current
      switcher.addEventListener("keydown", handleKeyDown)
      switcher.addEventListener("keydown", handleKeyDownAccordion)
      switcher.addEventListener("focusin", handleOnFocus)
      return () => {
        switcher.removeEventListener("keydown", handleKeyDown)
        switcher.removeEventListener("keydown", handleKeyDownAccordion)
        switcher.removeEventListener("focusin", handleOnFocus)
      }
    }
  }, [handleKeyDown, switcherRef])

  // this is to ensure that the focus state continues to track properly should the user
  // tab outside of the accordion, then tab back in, or swap between tabs and arrows while
  // navigating the accordion
  function handleOnFocus(event) {
    const focusedButtonIndex = parseInt(
      event.target.getAttribute("data-focus-index")
    )

    setCurrentAccordionFocus(focusedButtonIndex)
  }

  function handleSwitcherChange(i: number) {
    const contentItem = contentItems[i]

    setOpenItemIndex((prev) => {
      if (isMobile) {
        // if the item is open on the mobile accordion, close it
        if (i === prev && i !== null) {
          return null
        }
      }
      return i
    })

    setCurrentItemQuery((previousSlug) => {
      if (isMobile) {
        return contentItem?.slug === previousSlug ? null : contentItem?.slug
      }
      return contentItem?.slug
    })
  }

  const createContentSelector = (contentItems: ContentItem[]) => {
    const buttons = contentItems.map((contentItem, index) => {
      const { slug, buttonProps, buttonText, children } = contentItem
      // Set first button to active if no query param is set
      const isOpen =
        slug === currentItemQuery || (!currentItemQuery && index === 0)

      return (
        <li className="block w-full" key={index}>
          <button
            className={classNames(
              `block w-full text-lg py-3 pl-4 pr-6 rounded-tl-lg rounded-bl-lg text-left font-medium transition-colors duration-150 focus:outline-none focus:ring border-gray-300 border leading-tight border-r-0 overflow-hidden`,
              {
                "bg-gray-200": isOpen,
                "bg-white text-action-link hover:bg-gray-100": !isOpen,
              }
            )}
            onClick={() => {
              handleSwitcherChange(index)
            }}
            role="tab"
            aria-selected={isOpen}
            tabIndex={isOpen ? 0 : -1}
            id={`tab-${index}`}
            ref={(el) => (tabRefs.current[index] = el)}
            key={`solution-selector-${index}`}
            {...buttonProps}
            style={{
              whiteSpace: `break-spaces`,
              textOverflow: `ellipsis`,
            }}
          >
            {buttonText}
          </button>
        </li>
      )
    })
    return <>{buttons}</>
  }
  return (
    <div
      className={`flex flex-wrap md:flex-nowrap relative`}
      data-analytics-region={"Content Switcher"}
      id={`content-switcher-${param}`}
      role="tablist"
      ref={switcherRef}
      aria-owns={Array(contentItems.length)
        .map((_, index) => `tab-${index}`)
        .join(" ")}
    >
      <div className={`md:w-64 relative hidden md:block`}>
        <ul
          className={`space-y-4 md:sticky md:left-0 md:top-56 md:mt-8 md:mb-8`}
        >
          {createContentSelector(contentItems)}
        </ul>
      </div>
      <div className={`w-full md:w-auto md:flex-1 z-10`} role="tablist">
        {contentItems.map((contentItem, index) => {
          const isOpen = openItemIndex === index

          const { buttonProps, buttonText } = contentItem

          return (
            <React.Fragment key={index}>
              <button
                data-focus-index={index}
                className={classNames(
                  `block md:hidden relative text-gray-900 w-full text-lg text-left font-medium py-3 pr-4 focus:outline-none focus:ring leading-tight border-b overflow-hidden`,
                  {
                    "border-gray-300": !isOpen,
                    "border-transparent": isOpen,
                  }
                )}
                onClick={() => {
                  handleSwitcherChange(index)
                }}
                ref={(el) => (accordionRefs.current[index] = el)}
                aria-expanded={`${isOpen}`}
                aria-selected={isOpen}
                role="tab"
                id={`mobile-accordion-${index}`}
                {...buttonProps}
                style={{
                  whiteSpace: `break-spaces`,
                  textOverflow: `ellipsis`,
                }}
              >
                {buttonText}
                <i
                  className={classNames(
                    `block text-gray-700 icon-chevron-down leading-snug absolute py-3 top-0 right-0 transition-transform duration-150`,
                    {
                      "rotate-180": isOpen,
                      "rotate-0": !isOpen,
                    }
                  )}
                  style={{ width: `1em` }}
                ></i>
              </button>
              <div
                className={classNames(
                  `focus:outline-none focus:ring rounded-lg shadow-md border border-gray-300 overflow-hidden bg-white`,
                  isMobile && {
                    hidden: !isOpen,
                  },
                  !isMobile && {
                    "md:block":
                      isOpen || (openItemIndex === null && index === 0),
                    hidden: !isOpen && !(openItemIndex === null && index === 0),
                  }
                )}
                aria-labelledby={`tab-${index}`}
              >
                {contentItem.children}
              </div>
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

export default ContentSwitcher
